import { supabase } from "./supabase";

/**
 * Safely invoke a Supabase Edge Function with:
 *   - Proactive token refresh before expiry (prevents 401)
 *   - Automatic token refresh on 401 (fallback)
 *   - Extended timeout (90s) for long-running functions like AI calls
 *   - Network error retry (1 automatic retry on "Failed to fetch")
 *
 * USE THIS instead of supabase.functions.invoke() everywhere.
 *
 * @param {string} functionName - Edge Function slug
 * @param {object} options - { body, headers, method }
 * @returns {Promise<{ data, error }>}
 */
export async function invokeFunction(functionName, options = {}) {
  await ensureFreshToken();

  const result = await attemptInvoke(functionName, options);

  if (!result.error) {
    return result;
  }

  const errorMsg = result.error?.message || "";
  const statusCode = result.error?.context?.status;

  const is401 =
    statusCode === 401 ||
    errorMsg.includes("401") ||
    errorMsg.includes("Invalid JWT") ||
    errorMsg.includes("Unauthorized") ||
    errorMsg.includes("invalid claim") ||
    errorMsg.includes("token is expired");

  if (is401) {
    console.warn(
      `[invokeFunction] 401 on ${functionName}, force-refreshing session and retrying...`
    );
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      console.error("[invokeFunction] Session refresh failed:", refreshError.message);
      return result;
    }
    await delay(300);
    return await attemptInvoke(functionName, options);
  }

  const isNetworkError =
    errorMsg.includes("Failed to fetch") ||
    errorMsg.includes("NetworkError") ||
    errorMsg.includes("network") ||
    errorMsg.includes("AbortError") ||
    errorMsg.includes("The operation was aborted");

  if (isNetworkError) {
    console.warn(
      `[invokeFunction] Network error on ${functionName}, retrying once...`
    );
    await delay(2000);
    return await attemptInvoke(functionName, options);
  }

  return result;
}

/**
 * Proactively refresh the session if the access token is expired
 * or will expire within the next 60 seconds.
 * This prevents the Supabase Gateway from rejecting the request
 * with 401 "Invalid JWT" before our code even runs.
 */
async function ensureFreshToken() {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;

    if (!session) return;

    const expiresAt = session.expires_at;
    if (!expiresAt) return;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = expiresAt - nowSeconds;

    if (secondsUntilExpiry < 60) {
      console.log(
        `[invokeFunction] Token expires in ${secondsUntilExpiry}s, proactively refreshing...`
      );
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn("[invokeFunction] Proactive refresh failed:", error.message);
      }
    }
  } catch (err) {
    console.warn("[invokeFunction] ensureFreshToken error:", err.message);
  }
}

/**
 * Single invocation attempt using manual fetch with a 90-second timeout.
 * supabase.functions.invoke() does not let us control the timeout,
 * so we build the request ourselves using the session token.
 */
async function attemptInvoke(functionName, options = {}) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const supabaseToken = sessionData?.session?.access_token;
    const localToken = localStorage.getItem("obstuchim_token");
    const accessToken = localToken || supabaseToken;

    // Сначала пробуем встроенный серверный эндпоинт DeepSeek
    const localEndpoint = functionName === "deepseek-parse-cv" 
      ? "/api/ai/parse-cv" 
      : `/api/functions/v1/${functionName}`;

    try {
      const localController = new AbortController();
      const localTimeout = setTimeout(() => localController.abort(), 90000);

      const localResponse = await fetch(localEndpoint, {
        method: options.method || "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: localController.signal,
      });

      clearTimeout(localTimeout);

      if (localResponse.ok) {
        const localData = await localResponse.json();
        return { data: localData, error: null };
      }
    } catch (localErr) {
      console.warn(`[invokeFunction] Local route ${localEndpoint} failed, attempting remote fallback:`, localErr);
    }

    if (!accessToken) {
      return {
        data: null,
        error: { message: "No active session - user not authenticated" }
      };
    }

    const projectUrl = supabase.supabaseUrl || supabase.rest?.url?.replace("/rest/v1", "") || "";
    const functionUrl = `${projectUrl}/functions/v1/${functionName}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);

    const anonKey = supabase.supabaseKey || supabase.rest?.headers?.apikey || "";

    const response = await fetch(functionUrl, {
      method: options.method || "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
        "apikey": anonKey,
        ...options.headers
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      let errorBody;
      try {
        errorBody = JSON.parse(errorText);
      } catch {
        errorBody = { message: errorText };
      }

      const fullUrl = `${functionUrl}`;
      const payloadForLog = options.body ? JSON.stringify(options.body).substring(0, 200) : "none";
      console.error(
        `[invokeFunction] ${functionName} failed with status ${response.status}`,
        `\nURL: ${fullUrl}`,
        `\nResponse: ${errorText.substring(0, 300)}`,
        `\nPayload: ${payloadForLog}`
      );

      return {
        data: null,
        error: {
          message: errorBody?.error || errorBody?.message || `Request failed with status ${response.status} for URL: ${fullUrl}\n\nResponse:\n${errorText}\n\nPayload:\n${payloadForLog}`,
          context: { status: response.status }
        }
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (err) {
    if (err.name === "AbortError") {
      return {
        data: null,
        error: {
          message: "Request timed out after 90 seconds. The AI service may be under heavy load - please try again."
        }
      };
    }
    return {
      data: null,
      error: { message: err.message || "Failed to fetch" }
    };
  }
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
